import React from 'react'
import { useAuth } from '../contexts/AuthContext'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import ToolTour, { useToolTour, openTour, InfoTip, MarkerKey, ProfitSketch, RecommendedFilters } from '../components/ToolTour'
import BonusCard from './BonusCard'

const C = {
  border: '#1b2538',
  borderStrong: '#26354f',
  text: '#f1f5f9',
  muted: '#94a3b8',
  dim: '#64748b',
  green: '#34d399',
  amber: '#fbbf24',
}

// Education's worked example (lesson 2.3): a $50 TAB bonus in Melbourne Storm
// v Gold Coast Titans, carried through slides 1, 2 and 4
const BONUS = { book: 'TAB', pick: 'Over 58.5', odds: 5.5, stake: 50 }
const HEDGE = { book: 'Bet365', pick: 'Under 58.5', odds: 1.26, stake: 178.57 }
const PROFIT = BONUS.stake * (BONUS.odds - 1) - HEDGE.stake   // the same either way
const RETENTION = (PROFIT / BONUS.stake) * 100

const panel = {
  boxSizing: 'border-box', borderRadius: '14px',
  background: 'linear-gradient(180deg, #0f1a2e 0%, #0a1222 100%)', border: `1px solid ${C.borderStrong}`,
}
const label = { fontSize: '10px', fontWeight: 700, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.08em' }

// Slide 1 visual: the bonus bet and its cash hedge, and the same cash either way
function Conversion({ isMobile }) {
  const sides = [
    { ...BONUS, tag: 'Bonus bet', rgb: '245,158,11', money: `$${BONUS.stake} bonus` },
    { ...HEDGE, tag: 'Cash hedge', rgb: '148,163,184', money: `$${HEDGE.stake.toFixed(2)} cash` },
  ]
  return (
    <div style={{ width: '100%', maxWidth: isMobile ? 'none' : '440px' }}>
      <div style={{ ...label, textAlign: 'center', marginBottom: isMobile ? '8px' : '10px' }}>Total points · Storm v Titans</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isMobile ? '8px' : '12px' }}>
        {sides.map((s, i) => (
          <div key={s.book} className="tt-rise" style={{ ...panel, padding: isMobile ? '10px' : '12px 14px', border: `1px solid rgba(${s.rgb},0.35)`, animationDelay: `${i * 0.12}s` }}>
            <div style={{ fontSize: '9.5px', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: i ? C.muted : C.amber }}>{s.tag}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
              <img src={BOOKMAKER_LOGOS[s.book]} alt="" style={{ width: isMobile ? 22 : 26, height: isMobile ? 22 : 26, borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }} />
              <span style={{ fontSize: isMobile ? '12.5px' : '13.5px', fontWeight: 700, color: C.text, whiteSpace: 'nowrap' }}>{s.pick}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: isMobile ? '6px' : '8px', gap: '6px' }}>
              <span style={{ fontSize: isMobile ? '17px' : '19px', fontWeight: 800, color: C.text, fontVariantNumeric: 'tabular-nums' }}>{s.odds.toFixed(2)}</span>
              <span style={{ fontSize: isMobile ? '11.5px' : '12.5px', fontWeight: 700, color: i ? C.text : C.amber, whiteSpace: 'nowrap' }}>{s.money}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="tt-rise" style={{ ...panel, marginTop: isMobile ? '10px' : '12px', overflow: 'hidden', border: '1px solid rgba(16,185,129,0.35)', animationDelay: '0.45s' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
          {[BONUS, HEDGE].map((s, i) => (
            <div key={s.pick} style={{ padding: isMobile ? '8px 10px' : '9px 14px', borderLeft: i ? `1px solid ${C.border}` : 'none' }}>
              <div style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600, color: C.muted }}>If {s.pick.split(' ')[0]} wins</div>
              <div style={{ fontSize: isMobile ? '15px' : '16px', fontWeight: 800, color: C.green, marginTop: '2px' }}>+${PROFIT.toFixed(2)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// Slide 2 visual: how much of the bonus comes back as cash, against the
// same bands the page colours its retention badge with
const BANDS = [
  { from: 50, to: 60, rgb: '248,113,113', name: 'Skip' },
  { from: 60, to: 70, rgb: '96,165,250', name: 'Okay' },
  { from: 70, to: 80, rgb: '52,211,153', name: 'Good' },
  { from: 80, to: 100, rgb: '251,191,36', name: 'Great' },
]

function RetentionMeter({ isMobile }) {
  const at = (r) => `${((r - 50) / 50) * 100}%`
  return (
    <div style={{ width: '100%', maxWidth: isMobile ? 'none' : '440px' }}>
      <div className="tt-rise" style={{ ...panel, padding: isMobile ? '12px' : '14px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px' }}>
          <div>
            <div style={label}>$50 bonus bet</div>
            <div style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: 600, color: C.muted, marginTop: '2px' }}>becomes <span style={{ color: C.green, fontWeight: 800 }}>${PROFIT.toFixed(2)} cash</span></div>
          </div>
          <span style={{ fontSize: isMobile ? '24px' : '28px', fontWeight: 800, color: C.amber, fontVariantNumeric: 'tabular-nums' }}>{RETENTION.toFixed(2)}%</span>
        </div>
        {/* The band scale, with a pin at this conversion */}
        <div style={{ position: 'relative', marginTop: isMobile ? '14px' : '18px' }}>
          <div style={{ display: 'flex', height: '10px', borderRadius: '5px', overflow: 'hidden' }}>
            {BANDS.map(b => <div key={b.name} style={{ width: `${((b.to - b.from) / 50) * 100}%`, backgroundColor: `rgba(${b.rgb},0.55)` }} />)}
          </div>
          <div className="tt-pop" style={{
            position: 'absolute', top: '-5px', left: at(RETENTION), marginLeft: '-10px', width: '20px', height: '20px', borderRadius: '50%', boxSizing: 'border-box',
            backgroundColor: '#0b1324', border: `3px solid ${C.amber}`, boxShadow: '0 0 12px rgba(251,191,36,0.6)', animationDelay: '0.4s',
          }} />
          <div style={{ display: 'flex', marginTop: '6px' }}>
            {BANDS.map(b => (
              <div key={b.name} style={{ width: `${((b.to - b.from) / 50) * 100}%`, fontSize: isMobile ? '10px' : '11px', fontWeight: 700, color: `rgb(${b.rgb})` }}>
                {b.name} <span style={{ color: C.dim, fontWeight: 600 }}>{b.from}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// Slide 3. Bonus amount sits bottom right so its (i) opens inside the card.
function BonusAmountInfo() {
  return (
    <InfoTip label="What bonus amount means" title="Bonus amount" align="end">
      The value of the bonus bet you're converting. The hedge stake on every card is worked out from it, so change it to match each bonus.
    </InfoTip>
  )
}

const RECOMMENDED = [
  { label: 'Sports', value: 'All' },
  { label: 'Bonus bookie', value: 'Has your bonus' },
  { label: 'Hedge bookies', value: 'Your accounts' },
  { label: 'Retention %', value: '70% or more' },
  { label: 'Starts in', value: '0 to 24 hrs' },
  { label: 'Bonus amount', value: 'Your bonus', info: <BonusAmountInfo /> },
]

// Slide 4: the real card, carrying the same conversion
function OddsMovedInfo() {
  return (
    <InfoTip label="If the odds have moved" title="Odds moved?">
      Tap the arrow on the card to open the calculator and enter the new odds. It shows the new hedge stake and how much cash you'll keep.
    </InfoTip>
  )
}

function CardGuide({ isMobile }) {
  // Commence times are naive UTC, the same as the API sends them
  const commence = new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString().slice(0, 19)
  const opp = {
    sport: 'NRL', match: 'Melbourne Storm vs Gold Coast Titans', market_type: 'alternate_total_points', commence_time: commence,
    outcomes: [
      { bookmaker: BONUS.book, selection: BONUS.pick, odds: BONUS.odds, bet_type: 'BONUS' },
      { bookmaker: HEDGE.book, selection: HEDGE.pick, odds: HEDGE.odds, bet_type: 'HEDGE' },
    ],
  }
  return (
    // zoom (not transform) so the card's layout box shrinks too and the key
    // below stays in the stage. Two outcome rows make this card taller than
    // the single-pick tools' and its tags add height, hence the smaller zoom.
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ width: '100%', maxWidth: isMobile ? '220px' : '300px' }}>
        <div style={{ zoom: isMobile ? 0.62 : 0.72 }}>
          <BonusCard
            opp={opp}
            className="tt-card"
            oddsText={[BONUS.odds.toFixed(2), HEDGE.odds.toFixed(2)]}
            bonusStake={BONUS.stake.toFixed(2)}
            hedgeStake={HEDGE.stake.toFixed(2)}
            retention={RETENTION}
            markers={{ market: 1, bonus: 2, hedge: 3, odds: 4, stakes: 5, retention: 6, track: 7 }}
          />
        </div>
      </div>
      <MarkerKey isMobile={isMobile} labels={['Market', 'Bonus bet', 'Hedge bet', 'Odds', 'Stake', 'Retention', 'Track']} />
    </div>
  )
}

function bonusTourSlides(isMobile) {
  return [
    {
      title: 'How the Bonus Converter works',
      body: `Bonus bets don't return your stake when they win, so they're worth less than cash. The Bonus Converter turns them into cash: your bonus bet goes on one side at one bookie, and a cash hedge goes on the other side at another, sized so you get the same back whichever wins. Here a $${BONUS.stake} TAB bonus becomes $${PROFIT.toFixed(2)} either way.`,
      visual: <Conversion isMobile={isMobile} />,
    },
    {
      title: 'Retention',
      body: `Retention is how much of your bonus you keep as cash. ${RETENTION.toFixed(2)}% on a $${BONUS.stake} bonus is $${PROFIT.toFixed(2)}, locked in. Aim for 70% or more, and the card's corner turns gold once a conversion passes 80%.`,
      visual: <RetentionMeter isMobile={isMobile} />,
    },
    {
      title: 'Set your filters',
      body: 'Here\'s our recommended setup. Set the bonus bookmaker to the bookie holding your bonus bet, and the bonus amount to its value. Once it\'s set, tap Save Current and set it as your default so it loads every time.',
      visual: <RecommendedFilters rows={RECOMMENDED} isMobile={isMobile} />,
    },
    {
      title: 'Using the Bonus Converter',
      body: (
        <ol style={{ margin: 0, paddingLeft: '18px', display: 'grid', gap: '4px' }}>
          <li>Tap each bookie logo to open the game.</li>
          <li>Fill out both bet slips, your bonus on the bonus side and the hedge stake on the other, but don't confirm yet.</li>
          <li>Check the odds still match <OddsMovedInfo />, then place both straight after one another.</li>
          <li>Tap <strong style={{ color: C.text }}>+</strong> to track it. It's saved already settled, since the cash is locked in.</li>
        </ol>
      ),
      visual: <CardGuide isMobile={isMobile} />,
    },
    {
      title: 'You\'re ready to go',
      body: 'High retention conversions go fast, so act quickly when one appears. Every bonus you convert locks in cash with no swings, which makes this the safest way to turn over the bonus bets you collect from promos.',
      visual: <ProfitSketch isMobile={isMobile} steady headline="Cash from every bonus" sub="Locked in, no swings" />,
    },
  ]
}

export const openBonusTour = () => openTour('bonus')

export default function BonusTour({ isVisible, isMobile }) {
  const { hasAccess } = useAuth()
  const tour = useToolTour('bonus', { isVisible })
  return (
    <ToolTour
      open={tour.open}
      onClose={tour.close}
      slides={bonusTourSlides(isMobile)}
      eyebrow="Bonus Converter"
      isMobile={isMobile}
      finalAction={hasAccess('bonus') ? { label: 'Get into it' } : { label: 'Unlock with Promo Premium', onClick: () => { window.location.hash = '#/pricing' } }}
      learnMore={{ label: 'Learn more', onClick: () => window.dispatchEvent(new CustomEvent('bc-navigate', { detail: 'education' })) }}
    />
  )
}
